import React from 'react';
import { Alert, Box, Stack, Typography, alpha } from '@mui/material';
import SalesOpsHeader from './SalesOpsHeader';
import {
  Card,
  MethodNote,
  Shimmer,
  TableSkeleton,
  ThinEvidence,
  count,
  money,
  pct,
  tableSx,
} from './salesOpsKit';
import { fetchSalesOpsAgents } from '../../services/salesOperationsService';
import type { SalesOpsAgent, SalesOpsAgents } from '../../types/salesOperations';

type SortKey = 'margin_per_lead' | 'close_rate' | 'contact_rate' | 'avg_premium' | 'persistency';

/**
 * Agents -- margin by producer (handover 2.6).
 *
 * "The data must make close rate and margin diverge visibly — that's the
 * finding." So the table is sortable, and sorting it by close rate is the
 * fastest way to see that the order barely changes the margin column: the
 * best closer is not the best producer.
 *
 * The `finding` block under the table states that in words, computed from
 * the same rows rather than written as ad copy.
 */
export default function SalesOpsAgentsPage() {
  const [data, setData] = React.useState<SalesOpsAgents | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [sort, setSort] = React.useState<SortKey>('margin_per_lead');

  const load = React.useCallback((force = false) => {
    const controller = new AbortController();
    setLoading(true);
    fetchSalesOpsAgents(controller.signal, force)
      .then((d) => { setData(d); setError(null); })
      .catch((e: Error) => { if (e.name !== 'AbortError') setError(e.message); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  React.useEffect(() => load(), [load]);

  const pending = loading || !data;

  const rows = React.useMemo(() => {
    if (!data) return [];
    // Humans only. Auto-dialers and AI agents belong to a different question
    // and mixing them into a producer ranking makes both harder to read.
    return [...data.agents]
      .filter((a) => a.agent_type === 'Human' && a.margin_per_lead !== null)
      .sort((a, b) => (b[sort] ?? -Infinity) - (a[sort] ?? -Infinity) || 0);
  }, [data, sort]);

  return (
    <Box>
      <SalesOpsHeader
        title="Agents"
        description="Margin by producer, and every input that produced it"
        asOf={data?.anchor_date}
        computedAt={data?.computed_at}
        fromCache={data?.from_cache}
        onRefresh={() => load(true)}
        refreshing={loading}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!error && (
        <Stack spacing={2}>
          <Card title={`Agent performance — sorted by ${SORT_LABELS[sort]}`} dense>
            <Box component="table" sx={tableSx}>
              <thead>
                <tr>
                  <th>Agent</th>
                  <th className="num">Leads</th>
                  <SortHeader label="Contact rate" k="contact_rate" sort={sort} onSort={setSort} />
                  <SortHeader label="Close rate" k="close_rate" sort={sort} onSort={setSort} />
                  <SortHeader label="Avg premium" k="avg_premium" sort={sort} onSort={setSort} />
                  <th className="num">Commission</th>
                  <SortHeader label="Persistency" k="persistency" sort={sort} onSort={setSort} />
                  <th className="num">Agent cost</th>
                  <SortHeader
                    label="Margin / lead" k="margin_per_lead" sort={sort} onSort={setSort}
                  />
                  <th className="num">Margin / hour</th>
                </tr>
              </thead>
              <tbody>
                {!pending && rows.map((a, i) => (
                  <AgentRow
                    key={a.agent_id}
                    agent={a}
                    top={i === 0}
                    minPolicies={data!.min_policies_for_point_estimate}
                  />
                ))}
              </tbody>
            </Box>
            {pending && <TableSkeleton rows={8} cols={9} />}
            <Box sx={{ px: 2, pb: 2 }}>
              {pending ? <Shimmer width="80%" height={12} sx={{ mt: 1 }} /> : (
              <MethodNote>
                {data!.formula} Close rate and premium are measured over the recent
                contact window; persistency is measured over the whole book, because a
                policy needs twelve months before its outcome is knowable at all.
              </MethodNote>
              )}
            </Box>
          </Card>

          {pending && (
            <Card title="What this shows">
              <Shimmer height={13} sx={{ mb: 1 }} />
              <Shimmer height={13} sx={{ mb: 1 }} />
              <Shimmer width="70%" height={13} />
            </Card>
          )}
          {!pending && data?.finding && (
            <Card title="What this shows">
              <Typography sx={{ fontSize: 13.5, lineHeight: 1.7, color: 'text.secondary' }}>
                Close rate and margin do not move together.{' '}
                <Box component="strong" sx={{ color: 'text.primary' }}>
                  {data.finding.best_closer} closes {pct(data.finding.best_closer_close_rate, 0)} —
                  the best on the floor — and ranks #{data.finding.best_closer_margin_rank} of{' '}
                  {data.finding.of_agents} on margin
                </Box>{' '}
                at {money(data.finding.best_closer_margin)} per lead.{' '}
                <Box component="strong" sx={{ color: 'text.primary' }}>
                  {data.finding.top_margin_agent} closes{' '}
                  {pct(data.finding.top_margin_close_rate, 0)} and ranks first
                </Box>{' '}
                at {money(data.finding.top_margin)}, on business that carries a better
                carrier rate and stays on the books.
                <MethodNote>
                  Ranking agents on close rate sends your best leads to the wrong person.
                  This is the comparison a dialer cannot make: premium, commission and
                  lapse data live in the commission system, not the telephony platform.
                </MethodNote>
              </Typography>
            </Card>
          )}
        </Stack>
      )}
    </Box>
  );
}

const SORT_LABELS: Record<SortKey, string> = {
  margin_per_lead: 'margin per lead',
  close_rate: 'close rate',
  contact_rate: 'contact rate',
  avg_premium: 'average premium',
  persistency: 'persistency',
};

function SortHeader({
  label, k, sort, onSort,
}: {
  label: string; k: SortKey; sort: SortKey; onSort: (k: SortKey) => void;
}) {
  const active = sort === k;
  return (
    <Box
      component="th"
      className="num"
      onClick={() => onSort(k)}
      sx={{
        cursor: 'pointer',
        color: active ? 'text.primary !important' : undefined,
        '&:hover': { color: 'text.primary !important' },
      }}
    >
      {label}{active ? ' ▾' : ''}
    </Box>
  );
}

function AgentRow({
  agent, top, minPolicies,
}: {
  agent: SalesOpsAgent; top: boolean; minPolicies: number;
}) {
  return (
    <Box
      component="tr"
      sx={(t) => ({
        borderTop: `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}`,
        bgcolor: top ? alpha(t.palette.success.main, 0.09) : 'transparent',
      })}
    >
      <td><strong>{agent.name}</strong></td>
      <td className="num">{count(agent.leads_handled)}</td>
      <td className="num">{pct(agent.contact_rate)}</td>
      <td className="num">{pct(agent.close_rate, 0)}</td>
      <td className="num">{money(agent.avg_premium)}</td>
      <td className="num">{pct(agent.avg_commission_rate, 0)}</td>
      <td className="num">
        {pct(agent.persistency, 0)}
        {agent.thin_evidence && (
          <ThinEvidence observed={agent.iul_observed} minimum={minPolicies} />
        )}
      </td>
      <td className="num">{money(agent.agent_cost_per_lead)}</td>
      <td className="num">
        <Box
          component="strong"
          sx={(t) => ({ color: top ? t.palette.success.main : 'inherit' })}
        >
          {money(agent.margin_per_lead)}
        </Box>
      </td>
      <td className="num">{money(agent.margin_per_hour)}</td>
    </Box>
  );
}
